import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import './Home.css';

const Reveal = ({ children, delay = 0, className = '', direction = 'up' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const variants = {
    up: { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } },
    left: { hidden: { opacity: 0, x: -32 }, visible: { opacity: 1, x: 0 } },
    right: { hidden: { opacity: 0, x: 32 }, visible: { opacity: 1, x: 0 } },
    scale: { hidden: { opacity: 0, scale: 0.94 }, visible: { opacity: 1, scale: 1 } },
  };
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={variants[direction]}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const ventures = [
  { id: 'digital-services', name: 'Digital & Campaign Services', tag: 'Corporate Branding & Media Services', description: "Full-service political and brand campaign management. Built for Maharashtra's electoral and commercial landscape — precision, reach, and regional intelligence.", color: '#B86B4B' },
  { id: 'eco-warriors', name: 'Eco-Warriors\u2122', tag: 'EdTech & Impact Gaming', description: "Africa's first UNESCO-patronaged educational game. Deployed across 320+ schools in 4 countries, reaching 89,000+ students. Teaches recycling and waste management through gamification — and turns that data into carbon reduction intelligence for manufacturers.", color: '#4A7C59' },
  { id: 'discover-solapur', name: 'Discover Solapur\u2122', tag: 'Technology & Culture', description: 'A platform built to surface local culture, events, and experiences in Tier 2 India. The layer of discovery that cities like Solapur have always deserved.', color: '#2D5A7B' },
  { id: 'prismatex', name: 'PrismaTex', tag: 'Sustainable Manufacturing', description: "A 20,000 sq ft manufacturing facility in Kumbhari, Solapur. Established with \u20B92.62 Crore NCDC funding. Employs 280 women members. Prisma's commitment to community-first infrastructure in Tier 2 India.", color: '#8E5A3C' },
  { id: 'prisma-terra', name: 'Prisma Terra', tag: 'Real Estate & Infrastructure', description: 'NA plot development and real estate operations in Solapur. Long-horizon infrastructure investments with community-first thinking.', color: '#C4A484' },
  { id: 'prisma-studio', name: 'Prisma Studio', tag: 'Strategic Execution Layer', description: 'The internal systems behind every Prisma venture, selectively opened to external partners. Branding architecture, operational systems, and strategic advisory — not an agency, a precision layer.', color: '#7B5EA7' },
];

const Ventures = () => {
  const navigate = useNavigate();

  return (
    <div className="prisma-home" data-testid="ventures-page">
      <SEO
        title="Ventures | The Prisma Global"
        description="Six purpose-built ventures spanning EdTech, sustainable manufacturing, real estate, cultural technology, campaign management, and strategic advisory — built from Solapur, Maharashtra."
        canonical="https://www.theprismaglobal.com/ventures"
        keywords="Prisma Global ventures, Eco-Warriors UNESCO, PrismaTex Solapur manufacturing, Prisma Terra real estate, Discover Solapur, Digital Campaign Services Maharashtra, Prisma Studio advisory"
      />
      <Navbar />

      {/* Page Hero (dark) */}
      <section className="ventures-page-hero" data-testid="ventures-page-hero">
        <div className="section-inner">
          <Reveal>
            <p className="section-label">The Ventures</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="ventures-page-title">Five expressions of<br/>one architecture.</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="ventures-page-sub">
              Prisma is not a holding company. It is a single architecture rendered through six distinct ventures — each chosen for sectors where cultural understanding compounds into durable advantage.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Ventures Grid — same markup as homepage section */}
      <section className="prisma-ventures" id="ventures" data-testid="ventures-section">
        <div className="section-inner">
          <div className="ventures-grid">
            {ventures.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.08} direction="scale">
                <div className="venture-card" data-testid={`venture-card-${v.id}`} style={{ '--venture-accent': v.color }}>
                  <div className="venture-card-inner">
                    <span className="venture-tag">{v.tag}</span>
                    <h3 className="venture-name">{v.name}</h3>
                    <p className="venture-desc">{v.description}</p>
                    <span className="venture-cta">Explore <span className="cta-arrow">&rarr;</span></span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA back to contact */}
      <section className="ventures-page-cta section-light" data-testid="ventures-cta-section">
        <div className="section-inner ventures-page-cta-inner">
          <Reveal>
            <h2 className="ventures-cta-heading">Want to collaborate on a venture?</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <button
              className="cta-primary"
              data-testid="ventures-contact-cta"
              onClick={() => navigate('/contact')}
              type="button"
            >
              Start a Conversation &rarr;
            </button>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Ventures;
